<template>
  <div class="compair-layout--box">
    <div class="compair-layout--box-header">
      <h3>Filter</h3>
      <div class="vx-row">
        <div class="vx-col w-full">
          <DataInput
            v-model="filter.consultantID"
            type="model"
            placeholder="Berater"
            name="consultant"
            model-label="
                firstName
                lastName"
            :select-label="el => el.firstName + ' ' + el.lastName"
            gql-query="consultantsPage"
            gql-single-query="consultant"
            gql-filter-type="objectFilter"
            allow-null
          />
        </div>
        <div class="vx-col w-full lg:w-1/2 mt-2">
          <DataInput
            v-model="filter.startDate"
            type="date"
            label="Von"
            name="from"
            :config="{
              altFormat: 'd.m.Y H:i',
              dateFormat: 'Y-m-d H:i'
            }"
          />
        </div>
        <div class="vx-col w-full lg:w-1/2 mt-2">
          <DataInput
            v-model="filter.endDate"
            type="date"
            label="Bis"
            name="to"
            :config="{
              altFormat: 'd.m.Y H:i',
              dateFormat: 'Y-m-d H:i'
            }"
          />
        </div>
        <div class="vx-col w-full lg:w-1/3 mt-2">
          <DataInput
            v-model="filter.interval"
            type="select"
            :options="[
              { code: 1440, label: 'Täglich' },
              { code: 10080, label: '7 Tage' },
              { code: 43200, label: '30 Tage' },
              { code: -1, label: 'Monatlich' }
            ]"
            placeholder="Interval"
            name="interval"
            label="Interval"
          />
        </div>
        <div v-if="type === 'ACTION'" class="vx-col w-full lg:w-1/3 mt-2">
          <DataInput
            v-model="filter.typeID"
            type="model"
            class="w-full"
            label="Art"
            name="typeID"
            :model-label="'nameHistory'"
            :select-label="'nameHistory'"
            gql-query="actionTypes"
            allow-null
          />
        </div>
      </div>
    </div>
    <div class="compair-layout--box-body">
      <template v-if="datacollection">
        <ConsultantStatChart
          ref="chart"
          :chart-data="datacollection"
          :interval="filter.interval"
          :height="250"
          :max="chartMaxY"
        ></ConsultantStatChart>
      </template>
      <template v-else>
        Für die angegeben Filter gibt es aktuell keine Daten.
      </template>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import moment from "moment";
import DataInput from "@/components/ui/DataInput";
import ConsultantStatChart from "./charts/ConsultantStatChart";
import color from "../../assets/utils/color";
import { fillMissingDates } from "../../helpers/fillMissingDates";

export default {
  components: {
    DataInput,
    ConsultantStatChart
  },
  props: {
    datasetLabel: { type: String, default: "Einträge" },
    type: { type: String, required: true },
    maxY: { type: Number, default: null }
  },
  data() {
    return {
      internalMaxY: null,
      datacollection: null,
      filter: {
        consultantID: null,
        startDate: moment().subtract(30, "days"),
        endDate: null,
        interval: 10080,
        typeID: null
      }
    };
  },
  apollo: {
    actionTypes: {
      query: gql`
        query ActionTypes($page: Int!, $size: Int, $search: String) {
          actionTypes(page: $page, size: $size, search: $search) {
            results {
              id
              nameHistory
            }
          }
        }
      `,
      variables: {
        page: 1,
        size: 1000,
        search: null
      },
      update: data =>
        Object.fromEntries(
          data.actionTypes.results.map(at => [at.id, at.nameHistory])
        ),
      skip() {
        return this.type != "ACTION";
      }
    },
    me: {
      query: gql`
        query Me {
          me {
            id
            consultant {
              id
            }
          }
        }
      `,
      update(data) {
        if (data.me.consultant) {
          this.filter.consultantID = data.me.consultant.id;
        }

        return data.me;
      }
    },
    consultantStats: {
      query: gql`
        query ConsultantStats($filter: ConsultantStatFilter!) {
          consultantStats(filter: $filter) {
            datapoints {
              interval
              amount
              model
              field
              fieldValue
              time
              groupingValue
            }
          }
        }
      `,
      variables() {
        let metrics = [];

        if (this.type === "ACTION") {
          metrics.push({
            model: "ACTION",
            field: "type_id",
            value: this.filter.typeID
          });
        } else if (this.type === "APPLICANT") {
          metrics.push({
            model: "APPLICANT"
          });
        } else if (this.type === "APPLICATION") {
          metrics.push({
            model: "APPLICATION"
          });
        } else if (this.type === "VACANCY") {
          metrics.push({
            model: "VACANCY"
          });
        }

        return {
          filter: {
            consultantID: this.filter.consultantID,
            timeFilter: {
              interval:
                this.filter.interval > 0 ? this.filter.interval : undefined,
              months: this.filter.interval === -1,
              fromTime: this.filter.startDate,
              toTime: this.filter.endDate
            },
            metrics
          }
        };
      }
    }
  },
  computed: {
    chartMaxY() {
      return Math.max(this.internalMaxY, this.maxY);
    }
  },
  watch: {
    consultantStats(stats) {
      if (!stats.datapoints) {
        this.datacollection = null;
        return;
      }

      stats.datapoints.sort((a, b) =>
        a.time > b.time ? 1 : a.time < b.time ? -1 : 0
      );

      if (this.type == "ACTION") {
        let groupedStats = {};
        for (let dp of stats.datapoints) {
          if (!dp.fieldValue) {
            dp.fieldValue = "Keine Angabe";
          }
          const groupedStatsKey = dp.fieldValue;
          if (!groupedStats[groupedStatsKey]) {
            groupedStats[groupedStatsKey] = [dp];
          } else {
            groupedStats[groupedStatsKey].push(dp);
          }
        }
        let maxY = Math.max(...stats.datapoints.map(x => x.amount));
        let labels = Array.from(
          new Set(stats.datapoints.map(x => x.time))
        ).map(x => moment(x));
        labels.sort((a, b) =>
          a.valueOf() > b.valueOf() ? 1 : a.valueOf() < b.valueOf() ? -1 : 0
        );
        if (this.filter.interval == 1440) {
          labels = fillMissingDates(labels);
        }
        let datacollection = {
          labels,
          datasets: []
        };
        for (let [label, s] of Object.entries(groupedStats)) {
          let datapoints = [];
          let statsIndex = 0;
          for (let i = 0; i < labels.length; i++) {
            if (
              s[statsIndex] &&
              moment(s[statsIndex].time).valueOf() ==
                moment(labels[i]).valueOf()
            ) {
              datapoints.push(s[statsIndex].amount);
              statsIndex++;
            } else {
              datapoints.push(0);
            }
          }
          label = this.actionTypes[label] || "Keine Angabe";
          if (this.filter.typeID) {
            label = this.actionTypes[this.filter.typeID];
          }
          datacollection.datasets.push({
            label,
            borderColor: color.colorFromString(label),
            backgroundColor: color.colorFromString(label, 0.8),
            pointHoverBackgroundColor: "#000000",
            lineTension: 0,
            fill: true,
            data: datapoints
          });
        }
        let encountered = {};
        for (let dataset of datacollection.datasets) {
          if (encountered[dataset.label]) {
            encountered[dataset.label].data = encountered[
              dataset.label
            ].data.map((e, i) => e + dataset.data[i]);
          } else {
            encountered[dataset.label] = dataset;
          }
        }
        datacollection.datasets = Object.values(encountered);
        this.datacollection = datacollection;
        this.$emit("max-y-updated", maxY);
        return;
      }

      let consultants = {};
      for (let data of stats.datapoints) {
        if (consultants[data.groupingValue || "NULL"] === undefined) {
          consultants[data.groupingValue || "NULL"] = [];
        }
      }

      let labels = [];
      for (let data of stats.datapoints) {
        let date = moment(data.time);
        if (labels.findIndex(val => val.diff(date) === 0) === -1) {
          labels.push(date);
        }
      }
      labels.sort(function(a, b) {
        return a.isBefore(b) ? -1 : 1;
      });
      //TODO: fix this
      /*if (this.filter.interval == 1440) {
        labels = fillMissingDates(labels);
      }*/
      let maxValue = 1;
      for (let label of labels) {
        for (let data of stats.datapoints) {
          let time = moment(data.time);
          if (time.diff(label) === 0) {
            consultants[data.groupingValue || "NULL"].push(data.amount);
          }
        }
      }

      let datasets = [];
      for (let consultant of Object.keys(consultants)) {
        datasets.push({
          label: this.datasetLabel,
          borderColor: color.colorFromString(this.datasetLabel),
          data: consultants[consultant],
          fill: true,
          backgroundColor: color.colorFromString(this.datasetLabel, 0.8),
          pointHoverBackgroundColor: "#000000",
          lineTension: 0
        });

        const currentMaxValue = Math.max.apply(Math, consultants[consultant]);
        if (currentMaxValue > maxValue) {
          maxValue = currentMaxValue;
        }
      }

      this.internalMaxY = maxValue;
      this.$emit("max-y-updated", maxValue);

      this.datacollection = {
        labels,
        datasets
      };

      this.$nextTick().then(() => {
        this.$refs.chart.updateChart();
      });
    }
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/components/bi/main";
</style>
